import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Stack,
  CircularProgress
} from '@mui/material';
import WatchLaterOutlinedIcon from '@mui/icons-material/WatchLaterOutlined';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import { CustomBreadcrumbs, PageBanner, StartExamModal, ExamResultModal, ToastMessage, ConfirmationModal } from '../../common';
import teacherBannerBg from '../../../assets/images/teacher.jpg';
import ViewOptionsModal from './ViewOptionsModal';
import BeforeStartSummary from './BeforeStartSummary';
import examService from '../../../infrastructure/services/examService';
import { toSriLankanDate } from '../../../infrastructure/services/timeSyncService';

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<\/?[^>]+(>|$)/g, "");
};

const parseInstructions = (instructions) => {
  if (!instructions) return [];
  return instructions
    .split('\n')
    .map((line) => line.replace(/^[•\u2022\u00b7\-*\s]+/, '').trim())
    .filter(Boolean);
};

const mapChildExamType = (type) => {
  const normalized = (type || '').toLowerCase();
  if (normalized === 'mcq') return 'MCQ';
  if (normalized === 'essay') return 'Essay';
  if (normalized === 'structure') return 'Structure';
  return type || '';
};

const CHILD_PART_LABELS = {
  mcq: 'MCQ Section',
  essay: 'Essay Section',
  structure: 'Structured Section'
};

const CHILD_TYPE_CHIP = {
  mcq: { bgcolor: '#FFF1EE', color: '#FF502D' },
  essay: { bgcolor: '#F0FDFA', color: '#0D9488' },
  structure: { bgcolor: '#EEF2FF', color: '#6366F1' }
};

const parseMarksPercentage = (myMarks) => {
  const match = String(myMarks || '').match(/([\d.]+)\s*\/\s*([\d.]+)/);
  if (!match) return 0;
  const total = parseFloat(match[2]);
  if (!total) return 0;
  return Math.round((parseFloat(match[1]) / total) * 100);
};

const getGradeColor = (grade) => (grade === 'F' ? '#ef4444' : '#10b981');

const buildResultStats = (examResult, fallbackQuestionCount = 0) => {
  const rawQuestions = examResult?.questions || examResult?.detailedResults || [];

  if (examResult?.correctCount !== undefined && examResult?.correctCount !== null) {
    const correctCount = Number(examResult.correctCount);
    const skippedCount = Number(examResult.skippedCount);
    const wrongCount = Number(examResult.wrongCount);
    const totalCount = examResult?.gradingSummary?.totalQuestions || fallbackQuestionCount || rawQuestions.length || 0;
    const scorePercentage = examResult?.gradingSummary?.percentage !== undefined
      ? examResult.gradingSummary.percentage
      : 0;
    return { rawQuestions, correctCount, skippedCount, wrongCount, totalCount, scorePercentage };
  }

  const correctCount = examResult?.gradingSummary?.correctCount
    ?? rawQuestions.filter((q) => q.isCorrect === true).length;

  const hasIsSelected = rawQuestions.some((q) => q.isSelected !== undefined);

  const skippedCount = hasIsSelected
    ? rawQuestions.filter((q) => q.isSelected === false).length
    : rawQuestions.filter(
      (q) => q.studentAnswer === null || q.studentAnswer === undefined || q.studentAnswer === ''
    ).length;

  const wrongCount = examResult?.gradingSummary?.incorrectCount !== undefined
    ? Math.max(0, examResult.gradingSummary.incorrectCount - skippedCount)
    : (hasIsSelected
      ? rawQuestions.filter((q) => q.isSelected === true && q.isCorrect === false).length
      : Math.max(0, rawQuestions.length - correctCount - skippedCount));

  const totalCount = examResult?.gradingSummary?.totalQuestions || fallbackQuestionCount || rawQuestions.length || 0;
  const scorePercentage = examResult?.gradingSummary?.percentage !== undefined
    ? examResult.gradingSummary.percentage
    : 0;

  return { rawQuestions, correctCount, skippedCount, wrongCount, totalCount, scorePercentage };
};

const buildDynamicResultsFromStats = (stats) => ({
  questions: stats.rawQuestions,
  answers: stats.rawQuestions.reduce((acc, q, idx) => {
    acc[idx] = q.studentAnswer != null ? String(q.studentAnswer) : '';
    return acc;
  }, {}),
  totalCount: stats.totalCount,
  correctCount: stats.correctCount,
  skippedCount: stats.skippedCount,
  wrongCount: stats.wrongCount,
  scorePercentage: stats.scorePercentage
});

const viewAnswersButtonSx = {
  background: 'linear-gradient(90deg, #FF8B4A 0%, #FF6244 100%)',
  color: '#ffffff',
  fontWeight: 700,
  fontSize: '13.5px',
  fontFamily: 'Poppins',
  textTransform: 'none',
  borderRadius: '9999px',
  px: 3,
  py: 1,
  boxShadow: '0 6px 16px rgba(255, 98, 68, 0.25)',
  '&:hover:not(:disabled)': {
    background: 'linear-gradient(90deg, #ff9b5f 0%, #ff7357 100%)',
    boxShadow: '0 8px 20px rgba(255, 98, 68, 0.35)',
    transform: 'translateY(-1.5px)'
  },
  '&:active:not(:disabled)': {
    transform: 'translateY(0)'
  },
  '&:disabled': {
    background: '#cbd5e1',
    color: '#94a3b8',
    boxShadow: 'none',
    cursor: 'not-allowed'
  },
  transition: 'all 0.2s ease'
};

const startButtonSx = {
  background: 'linear-gradient(90deg, #FF8B4A 0%, #FF6244 100%)',
  color: '#ffffff',
  fontWeight: 750,
  fontSize: '14.5px',
  fontFamily: 'Poppins',
  textTransform: 'none',
  borderRadius: '9999px',
  px: 4,
  py: 1.75,
  boxShadow: '0 8px 24px rgba(255, 98, 68, 0.35)',
  '&:hover': {
    background: 'linear-gradient(90deg, #ff9b5f 0%, #ff7357 100%)',
    boxShadow: '0 12px 28px rgba(255, 98, 68, 0.45)',
    transform: 'translateY(-2px)'
  },
  '&:active': {
    transform: 'translateY(0)'
  },
  transition: 'all 0.25s ease'
};
export default function ExamSummaryPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Retrieve exam details from router state or use defaults
  const exam = location.state?.exam || ""

  const isCompleted = location.state?.isCompleted || false;
  const results = location.state?.results || null;
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [isViewOptionsModalOpen, setIsViewOptionsModalOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [examDetails, setExamDetails] = useState(null);
  const [examResults, setExamResults] = useState(null);
  const [childExamResults, setChildExamResults] = useState({});
  const [viewOptionsChild, setViewOptionsChild] = useState(null);
  const [isExpired, setIsExpired] = useState(
    location.state?.exam?.isExpired || location.state?.exam?.status === 'expired'
  );

  const [institute, setInstitute] = useState('Lyceum Rathnapura');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMcqConfirmOpen, setIsMcqConfirmOpen] = useState(false);
  const [selectedChildExam, setSelectedChildExam] = useState(null);

  // Toast notification state
  const [toast, setToast] = useState({ open: false, message: '', severity: 'error' });
  const handleCloseToast = () => setToast((prev) => ({ ...prev, open: false }));

  // classExamId — prefer URL param, fall back to state
  const { classExamId: paramExamId } = useParams();
  const classExamId = paramExamId || exam?.id;

  useEffect(() => {
    const storedBranch = localStorage.getItem('branchName');
    if (storedBranch) {
      setInstitute(storedBranch);
    }
  }, []);

  useEffect(() => {
    if (!classExamId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch details
        const detailsData = await examService.getExamDetails(classExamId);
        let detailsContent = null;
        if (detailsData && detailsData.isSuccessful) {
          detailsContent = detailsData.content;
          setExamDetails(detailsContent);
          if (detailsContent?.isExpired || detailsContent?.status === 'expired') {
            setIsExpired(true);
          }
        } else if (detailsData && !detailsData.isSuccessful) {
          const errMsg = detailsData.message || '';
          if (errMsg.toLowerCase().includes('expired')) {
            setIsExpired(true);
          }
          setToast({ open: true, message: errMsg || 'Failed to load exam details.', severity: 'error' });
        }

        if (isCompleted) {
          const isMixedDetails = detailsContent?.examType?.toLowerCase() === 'mixed'
            && Array.isArray(detailsContent?.childExams)
            && detailsContent.childExams.length > 0;

          if (isMixedDetails) {
            const resultsMap = {};
            await Promise.all(
              detailsContent.childExams.map(async (child) => {
                try {
                  const childRes = await examService.getExamResult(classExamId, child.examId);
                  if (childRes?.isSuccessful) {
                    resultsMap[child.examId] = childRes.content;
                  }
                } catch (childErr) {
                  console.error(`Failed to fetch result for child exam ${child.examId}:`, childErr);
                }
              })
            );
            setChildExamResults(resultsMap);
            setExamResults(null);
          } else {
            setChildExamResults({});
            const resultsData = await examService.getExamResult(classExamId);
            if (resultsData && resultsData.isSuccessful) {
              setExamResults(resultsData.content);
            } else if (resultsData && !resultsData.isSuccessful) {
              const errMsg = resultsData.message || '';
              if (errMsg.toLowerCase().includes('expired')) {
                setIsExpired(true);
              }
              setToast({ open: true, message: errMsg || 'Failed to load exam results.', severity: 'error' });
            }
          }
        } else {
          setChildExamResults({});
        }
      } catch (err) {
        console.error('Failed to fetch exam data:', err);
        const errMsg = err.message || '';
        if (errMsg.toLowerCase().includes('expired')) {
          setIsExpired(true);
        }
        setToast({ open: true, message: errMsg || 'Failed to load exam data.', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [classExamId, isCompleted]);

  // Format date utility
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = toSriLankanDate(dateStr);
      const day = date.getDate();
      const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
      ];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();

      const getOrdinal = (d) => {
        if (d > 3 && d < 21) return 'th';
        switch (d % 10) {
          case 1: return "st";
          case 2: return "nd";
          case 3: return "rd";
          default: return "th";
        }
      };
      return `${day}${getOrdinal(day)} ${month} ${year}`;
    } catch (e) {
      return dateStr;
    }
  };

  const rawType = examDetails?.examType || exam.type || '';
  const isMixedExam = rawType.toLowerCase() === 'mixed'
    && Array.isArray(examDetails?.childExams)
    && examDetails.childExams.length > 0;
  const childExams = isMixedExam ? examDetails.childExams : [];
  const mixedTotalQuestions = childExams.reduce((sum, child) => sum + (child.questionCount || 0), 0);
  const displayType = rawType.toLowerCase() === 'mixed'
    ? 'Mixed'
    : rawType.toLowerCase() === 'structure'
      ? 'Structured'
      : rawType.toLowerCase() === 'mcq'
        ? 'MCQ'
        : rawType.toLowerCase() === 'essay'
          ? 'Essay'
          : rawType;

  // Normalize: MCQ uses `detailedResults`, Essay/Structured use `questions`
  const rawQuestions = examResults?.questions || examResults?.detailedResults || [];

  const correctCount = examResults?.correctCount !== undefined && examResults?.correctCount !== null
    ? Number(examResults.correctCount)
    : (examResults?.gradingSummary?.correctCount
      ?? rawQuestions.filter(q => q.isCorrect === true).length);

  const hasIsSelected = rawQuestions.some(q => q.isSelected !== undefined);

  const skippedCount = examResults?.skippedCount !== undefined && examResults?.skippedCount !== null
    ? Number(examResults.skippedCount)
    : (hasIsSelected
      ? rawQuestions.filter(q => q.isSelected === false).length
      : rawQuestions.filter(
        q => q.studentAnswer === null || q.studentAnswer === undefined || q.studentAnswer === ''
      ).length);

  const wrongCount = examResults?.wrongCount !== undefined && examResults?.wrongCount !== null
    ? Number(examResults.wrongCount)
    : (examResults?.gradingSummary?.incorrectCount !== undefined
      ? Math.max(0, examResults.gradingSummary.incorrectCount - skippedCount)
      : (hasIsSelected
        ? rawQuestions.filter(q => q.isSelected === true && q.isCorrect === false).length
        : Math.max(0, rawQuestions.length - correctCount - skippedCount)));

  const scorePercentage = examResults?.gradingSummary?.percentage !== undefined
    ? examResults.gradingSummary.percentage
    : 0;

  const calculatedGrade = examResults?.gradingSummary?.grade || examDetails?.myGrade || '-';
  const overallGrade = isMixedExam ? (examDetails?.myGrade || calculatedGrade) : calculatedGrade;
  const overallMarksDisplay = isMixedExam && examDetails?.myMarks
    ? `${parseMarksPercentage(examDetails.myMarks)}%`
    : `${scorePercentage}%`;

  const totalCount = examResults?.gradingSummary?.totalQuestions || examDetails?.questionCount || rawQuestions.length || 0;

  // Construct dynamic results to be passed to modal and breakdowns
  const dynamicResults = examResults ? {
    questions: rawQuestions,
    answers: rawQuestions.reduce((acc, q, idx) => {
      acc[idx] = q.studentAnswer != null ? String(q.studentAnswer) : '';
      return acc;
    }, {}),
    totalCount: examResults.gradingSummary?.totalQuestions || rawQuestions.length || 0,
    correctCount,
    skippedCount,
    wrongCount,
    scorePercentage: examResults.gradingSummary?.percentage !== undefined ? examResults.gradingSummary.percentage : 0
  } : results;

  const examName = stripHtml(examResults?.exam?.title || examResults?.examMetadata?.title || exam.name);

  const guidelines = examDetails?.instructions
    ? parseInstructions(examDetails.instructions)
    : [
      'Click Start to begin the exam.',
      'Your timer starts as soon as you enter.',
      'Select the correct answer for each question.',
      'You can review and change your answers before submitting.',
      'You cannot pause or restart once started.',
      'Manage your time and submit before the timer ends.'
    ];

  const breadcrumbItems = [
    { label: 'Home', href: '/dashboard' },
    { label: 'Exam Management', href: '/exam-manager' },
    { label: 'Exam Summary' }
  ];

  const handleOpenStartModal = (childExam = null) => {
    const examType = childExam
      ? mapChildExamType(childExam.examType)
      : rawType;
    const isMcq = examType?.toUpperCase() === 'MCQ';

    setSelectedChildExam(childExam);

    if (isMcq) {
      // MCQ: show a simple confirmation modal instead of written exam instructions popup
      setIsMcqConfirmOpen(true);
    } else {
      // Written exams: show instructions popup first
      setIsModalOpen(true);
    }
  };

  const handleConfirmStartMcq = () => {
    setIsMcqConfirmOpen(false);

    if (selectedChildExam) {
      const childType = mapChildExamType(selectedChildExam.examType);
      navigate(`/exam-manager/take-exam/${classExamId}`, {
        state: {
          exam: {
            ...exam,
            id: classExamId,
            examId: selectedChildExam.examId,
            childExamId: selectedChildExam.examId,
            name: stripHtml(selectedChildExam.examTitle) || examName,
            type: childType,
            duration: selectedChildExam.duration || examDetails?.duration || exam.duration,
            subject: examDetails?.subjectName || exam.subject,
            teacher: examDetails?.teacherName || exam.teacher
          }
        }
      });
      setSelectedChildExam(null);
    } else {
      navigate(`/exam-manager/take-exam/${classExamId}`, {
        state: {
          exam: {
            ...exam,
            examId: examDetails?.examId || exam?.examId,
            name: examName,
            type: rawType,
            duration: examDetails?.duration || exam.duration
          }
        }
      });
    }
  };

  const handleCloseStartModal = () => {
    setIsModalOpen(false);
    setSelectedChildExam(null);
  };

  const handleStartExam = () => {
    setIsModalOpen(false);

    if (selectedChildExam) {
      const childType = mapChildExamType(selectedChildExam.examType);
      navigate(`/exam-manager/take-exam/${classExamId}`, {
        state: {
          exam: {
            ...exam,
            id: classExamId,
            examId: selectedChildExam.examId,
            childExamId: selectedChildExam.examId,
            name: stripHtml(selectedChildExam.examTitle) || examName,
            type: childType,
            duration: selectedChildExam.duration || examDetails?.duration || exam.duration,
            subject: examDetails?.subjectName || exam.subject,
            teacher: examDetails?.teacherName || exam.teacher
          }
        }
      });
      setSelectedChildExam(null);
      return;
    }

    navigate(`/exam-manager/take-exam/${classExamId}`, {
      state: {
        exam: {
          ...exam,
          examId: examDetails?.examId || exam?.examId,
          name: examName,
          type: rawType,
          duration: examDetails?.duration || exam.duration
        }
      }
    });
  };

  const handleViewChildAnswers = (childExam) => {
    const childType = mapChildExamType(childExam.examType);
    const childTitle = stripHtml(childExam.examTitle) || CHILD_PART_LABELS[childExam.examType] || childType;
    const childResult = childExamResults[childExam.examId];
    const stats = buildResultStats(childResult, childExam.questionCount || 0);
    const childScorePercentage = stats.scorePercentage || parseMarksPercentage(childExam.myMarks);
    const childDynamicResults = buildDynamicResultsFromStats({
      ...stats,
      scorePercentage: childScorePercentage
    });

    if (childType === 'MCQ') {
      navigate(`/exam-manager/answer-breakdown/${classExamId}?examId=${childExam.examId}`, {
        state: {
          exam: {
            ...exam,
            name: childTitle,
            type: childType,
            examId: childExam.examId
          },
          results: childDynamicResults
        }
      });
      return;
    }

    setViewOptionsChild(childExam);
    setIsViewOptionsModalOpen(true);
  };

  const activeModalType = selectedChildExam
    ? mapChildExamType(selectedChildExam.examType)
    : rawType;
  const activeModalDuration = selectedChildExam?.duration || examDetails?.duration || exam.duration || '1 H 30 Min';
  const activeModalQuestionsCount = selectedChildExam
    ? `${selectedChildExam.questionCount || 0} Questions`
    : (examDetails?.questionCount ? `${examDetails.questionCount} Questions` : (rawType.toUpperCase() === 'MCQ' ? '12 Questions' : '15 Questions'));
  const activeModalInstructions = selectedChildExam
    ? parseInstructions(selectedChildExam.instructions)
    : guidelines;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 15 }}>
        <CircularProgress sx={{ color: '#FF502D' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 5, fontFamily: 'Poppins, sans-serif' }}>
      {/* Breadcrumbs */}
      <CustomBreadcrumbs items={breadcrumbItems} sx={{ mb: 2 }} />

      {/* Page Banner */}
      <PageBanner
        title="Exam Summary"
        description={`${examDetails?.subjectName || exam.subject} - ${exam.topic || examDetails?.gradeName || 'General'}`}
        backgroundImage={teacherBannerBg}
      >
        <Button
          variant="outlined"
          onClick={() => {
            const fromRoute = location.state?.fromRoute;
            if (fromRoute) {
              navigate(fromRoute);
            } else {
              navigate(-1);
            }
          }}
          sx={{
            borderColor: '#ffffff',
            borderWidth: '1px',
            color: '#ffffff',
            borderRadius: '14px',
            px: 3,
            py: 0.8,
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'none',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(4px)',
            transition: 'all 0.25s ease',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            '&:hover': {
              borderColor: '#ffffff',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'translateX(-2px)'
            }
          }}
        >
          <KeyboardArrowLeftRoundedIcon sx={{ fontSize: '18px' }} /> Back
        </Button>
      </PageBanner>

      {/* Main Grid Wrapper */}
      {isCompleted ? (
        <Box
          sx={{
            display: 'grid',
            gap: 4,
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            alignItems: isMixedExam ? 'flex-start' : 'stretch',
            width: '100%'
          }}
        >
          {/* Left Column: Gradient Card with Details */}
          <Paper
            elevation={0}
            sx={{
              p: 3.5,
              height: isMixedExam ? 'auto' : '100%',
              alignSelf: isMixedExam ? 'flex-start' : 'stretch',
              boxSizing: 'border-box',
              background: 'linear-gradient(135deg, #e0f7fa 0%, #e8eaf6 50%, #f3e5f5 100%)',
              border: '1.5px solid #e2e8f0',
              borderRadius: '24px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxShadow: '0 10px 30px -10px rgba(100, 116, 139, 0.12)'
            }}
          >
            {/* Top Row with Title & Icon */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '20px', color: '#1e293b', fontFamily: 'Poppins', maxWidth: '80%', lineHeight: 1.3 }}>
                {examName}
              </Typography>
              <Box
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.65)',
                  p: 1,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(4px)'
                }}
              >
                <WatchLaterOutlinedIcon sx={{ color: '#4f46e5', fontSize: '20px' }} />
              </Box>
            </Box>

            {/* Middle Row: Score Pill Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 4 }}>
              {/* Grade Box */}
              <Box
                sx={{
                  bgcolor: '#ffffff',
                  borderRadius: '16px',
                  p: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.02)'
                }}
              >
                <Typography sx={{ fontSize: '13px', color: '#64748b', fontWeight: 600, fontFamily: 'Poppins' }}>
                  Your Grade
                </Typography>
                <Typography sx={{ fontSize: '32px', fontWeight: 800, color: getGradeColor(overallGrade), fontFamily: 'Poppins', lineHeight: 1 }}>
                  {overallGrade}
                </Typography>
              </Box>

              {/* Marks Box */}
              <Box
                sx={{
                  bgcolor: '#ffffff',
                  borderRadius: '16px',
                  p: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.02)'
                }}
              >
                <Typography sx={{ fontSize: '13px', color: '#64748b', fontWeight: 600, fontFamily: 'Poppins' }}>
                  Your Marks
                </Typography>
                <Typography sx={{ fontSize: '32px', fontWeight: 800, color: '#8b5cf6', fontFamily: 'Poppins', lineHeight: 1 }}>
                  {overallMarksDisplay}
                </Typography>
              </Box>
            </Box>

            {/* Bottom Row: Metadata Key Value Pairs */}
            <Stack spacing={2} sx={{ bgcolor: 'rgba(255, 255, 255, 0.5)', p: 2.5, borderRadius: '20px', backdropFilter: 'blur(4px)' }}>
              {[
                { label: 'Teacher', value: examDetails?.teacherName || exam.teacher || 'Mr. Rohan Silva' },
                { label: 'Institute', value: examDetails?.institute || institute },
                { label: 'Exam Type', value: displayType },
                { label: 'Exam Date', value: formatDate(examDetails?.examDate) || '14th August 2025' }
              ].map((meta, idx) => (
                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '12.5px', color: '#64748b', fontWeight: 500, fontFamily: 'Poppins' }}>
                    {meta.label}
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: '#1e293b', fontWeight: 600, fontFamily: 'Poppins', textAlign: 'right' }}>
                    {meta.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>

          {/* Right Column: Guidelines/Scorecard and Action */}
          {isMixedExam ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', px: 0.5 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '18px', color: '#1e293b', fontFamily: 'Poppins', mr: 'auto', alignSelf: 'center' }}>
                  {displayType}
                </Typography>
                <Chip
                  label={`${mixedTotalQuestions} Questions`}
                  size="small"
                  sx={{
                    bgcolor: '#f3e8ff',
                    color: '#9333EA',
                    fontWeight: 700,
                    fontSize: '12px',
                    fontFamily: 'Poppins',
                    borderRadius: '999px',
                    height: '28px',
                    px: 1.25
                  }}
                />
              </Box>

              <Box sx={{ borderTop: '1px solid #e2e8f0', width: '100%' }} />

              <Stack spacing={2}>
                {childExams.map((childExam) => {
                  const childTypeKey = (childExam.examType || '').toLowerCase();
                  const childType = mapChildExamType(childExam.examType);
                  const childLabel = CHILD_PART_LABELS[childTypeKey] || childType;
                  const childTitle = stripHtml(childExam.examTitle) || childLabel;
                  const childResult = childExamResults[childExam.examId];
                  const stats = buildResultStats(childResult, childExam.questionCount || 0);
                  const childScorePercentage = stats.scorePercentage || parseMarksPercentage(childExam.myMarks);
                  const childGrade = childExam.myGrade || childResult?.gradingSummary?.grade || '-';
                  const typeChip = CHILD_TYPE_CHIP[childTypeKey] || { bgcolor: '#f1f5f9', color: '#475569' };
                  const safeTotalCount = stats.totalCount || childExam.questionCount || 0;

                  const correctCount = childResult ? stats.correctCount : (childExam.correctCount ?? 0);
                  const wrongCount = childResult ? stats.wrongCount : (childExam.wrongCount ?? 0);
                  const skippedCount = childResult ? stats.skippedCount : (childExam.skippedCount ?? 0);
                  const showProgressBar = safeTotalCount > 0 && (childType === 'MCQ' || childType === 'Essay' || childType === 'Structure');

                  return (
                    <Paper
                      key={childExam.examId}
                      elevation={0}
                      sx={{
                        p: 3,
                        boxSizing: 'border-box',
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '24px',
                        bgcolor: '#ffffff',
                        boxShadow: '0 10px 30px -10px rgba(100, 116, 139, 0.05)'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                          <Typography sx={{ fontWeight: 800, fontSize: '16px', color: '#1e293b', fontFamily: 'Poppins' }}>
                            {childTitle}
                          </Typography>
                          <Chip
                            label={childLabel}
                            size="small"
                            sx={{
                              bgcolor: typeChip.bgcolor,
                              color: typeChip.color,
                              fontWeight: 700,
                              fontSize: '11px',
                              fontFamily: 'Poppins',
                              borderRadius: '8px',
                              height: '24px'
                            }}
                          />
                          <Chip
                            label={`${childScorePercentage}%`}
                            size="small"
                            sx={{
                              bgcolor: '#f3e5f5',
                              color: '#8b5cf6',
                              fontWeight: 700,
                              fontSize: '12px',
                              fontFamily: 'Poppins',
                              borderRadius: '999px',
                              height: '28px',
                              px: 1.25
                            }}
                          />
                        </Stack>
                        <Button
                          variant="contained"
                          disabled={isExpired}
                          onClick={() => handleViewChildAnswers(childExam)}
                          startIcon={
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                              <line x1="16" y1="13" x2="8" y2="13" />
                              <line x1="16" y1="17" x2="8" y2="17" />
                              <polyline points="10 9 9 9 8 9" />
                            </svg>
                          }
                          sx={viewAnswersButtonSx}
                        >
                          View Answers
                        </Button>
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2.5 }}>
                        <Box sx={{ bgcolor: '#f8fafc', borderRadius: '14px', p: 2, border: '1px solid #e2e8f0' }}>
                          <Typography sx={{ fontSize: '12px', color: '#64748b', fontWeight: 600, fontFamily: 'Poppins', mb: 0.5 }}>
                            Grade
                          </Typography>
                          <Typography sx={{ fontSize: '24px', fontWeight: 800, color: getGradeColor(childGrade), fontFamily: 'Poppins' }}>
                            {childGrade}
                          </Typography>
                        </Box>
                        <Box sx={{ bgcolor: '#f8fafc', borderRadius: '14px', p: 2, border: '1px solid #e2e8f0' }}>
                          <Typography sx={{ fontSize: '12px', color: '#64748b', fontWeight: 600, fontFamily: 'Poppins', mb: 0.5 }}>
                            Marks
                          </Typography>
                          <Typography sx={{ fontSize: '18px', fontWeight: 800, color: '#8b5cf6', fontFamily: 'Poppins' }}>
                            {childExam.myMarks || `${childScorePercentage}%`}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: showProgressBar ? 2.5 : 0 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '28px', color: '#1e293b', fontFamily: 'Poppins', lineHeight: 1 }}>
                          {safeTotalCount}
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: '#94a3b8', fontWeight: 550, fontFamily: 'Poppins' }}>
                          Total Questions
                        </Typography>
                      </Box>

                      {showProgressBar && (
                        <>
                          <Box sx={{ display: 'flex', height: '7px', borderRadius: '9999px', overflow: 'hidden', mb: 3, bgcolor: '#cbd5e1' }}>
                            {correctCount > 0 && (
                              <Box sx={{ width: `${(correctCount / safeTotalCount) * 100}%`, bgcolor: '#00ba88', height: '100%' }} />
                            )}
                            {wrongCount > 0 && (
                              <Box sx={{ width: `${(wrongCount / safeTotalCount) * 100}%`, bgcolor: '#ef4444', height: '100%' }} />
                            )}
                            {skippedCount > 0 && (
                              <Box sx={{ width: `${(skippedCount / safeTotalCount) * 100}%`, bgcolor: '#e2e8f0', height: '100%' }} />
                            )}
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#00ba88' }} />
                              <Typography sx={{ fontSize: '13px', fontFamily: 'Poppins', color: '#475569', fontWeight: 600 }}>
                                Correct <span style={{ color: '#94a3b8', marginLeft: '6px' }}>{correctCount.toString().padStart(2, '0')}</span>
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
                              <Typography sx={{ fontSize: '13px', fontFamily: 'Poppins', color: '#475569', fontWeight: 600 }}>
                                Wrong <span style={{ color: '#94a3b8', marginLeft: '6px' }}>{wrongCount.toString().padStart(2, '0')}</span>
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#cbd5e1' }} />
                              <Typography sx={{ fontSize: '13px', fontFamily: 'Poppins', color: '#475569', fontWeight: 600 }}>
                                Skipped <span style={{ color: '#94a3b8', marginLeft: '6px' }}>{skippedCount.toString().padStart(2, '0')}</span>
                              </Typography>
                            </Box>
                          </Box>
                        </>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: '100%',
                boxSizing: 'border-box',
                border: '1.5px solid #e2e8f0',
                borderRadius: '24px',
                bgcolor: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: '0 10px 30px -10px rgba(100, 116, 139, 0.05)'
              }}
            >
              <Box>
                {/* Type, Score percentage chip and View Answers Button */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 3.5 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography sx={{ fontWeight: 800, fontSize: '18px', color: '#1e293b', fontFamily: 'Poppins' }}>
                      {displayType}
                    </Typography>
                    <Chip
                      label={`${scorePercentage}%`}
                      size="small"
                      sx={{
                        bgcolor: '#f3e5f5',
                        color: '#8b5cf6',
                        fontWeight: 700,
                        fontSize: '12px',
                        fontFamily: 'Poppins',
                        borderRadius: '999px',
                        height: '28px',
                        px: 1.25
                      }}
                    />
                  </Stack>
                  <Button
                    variant="contained"
                    disabled={isExpired}
                    onClick={() => {
                      if (rawType.toUpperCase() === 'MCQ') {
                        const mainExamId = exam?.examId || examDetails?.examId;
                        const query = mainExamId ? `?examId=${mainExamId}` : '';
                        navigate(`/exam-manager/answer-breakdown/${classExamId}${query}`, {
                          state: {
                            exam: { ...exam, name: examName, type: rawType },
                            results: dynamicResults
                          }
                        });
                      } else {
                        setIsViewOptionsModalOpen(true);
                      }
                    }}
                    startIcon={
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                      </svg>
                    }
                    sx={{
                      background: 'linear-gradient(90deg, #FF8B4A 0%, #FF6244 100%)',
                      color: '#ffffff',
                      fontWeight: 700,
                      fontSize: '13.5px',
                      fontFamily: 'Poppins',
                      textTransform: 'none',
                      borderRadius: '9999px',
                      px: 3,
                      py: 1,
                      boxShadow: '0 6px 16px rgba(255, 98, 68, 0.25)',
                      '&:hover:not(:disabled)': {
                        background: 'linear-gradient(90deg, #ff9b5f 0%, #ff7357 100%)',
                        boxShadow: '0 8px 20px rgba(255, 98, 68, 0.35)',
                        transform: 'translateY(-1.5px)'
                      },
                      '&:active:not(:disabled)': {
                        transform: 'translateY(0)'
                      },
                      '&:disabled': {
                        background: '#cbd5e1',
                        color: '#94a3b8',
                        boxShadow: 'none',
                        cursor: 'not-allowed'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    View Answers
                  </Button>
                </Box>

                {/* Total Questions Count */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 3 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '32px', color: '#1e293b', fontFamily: 'Poppins', lineHeight: 1 }}>
                    {totalCount}
                  </Typography>
                  <Typography sx={{ fontSize: '14px', color: '#94a3b8', fontWeight: 550, fontFamily: 'Poppins' }}>
                    Total Questions
                  </Typography>
                </Box>

                {/* Segmented Horizontal Bar */}
                <Box sx={{ display: 'flex', height: '7px', borderRadius: '9999px', overflow: 'hidden', mb: 4.5, bgcolor: '#cbd5e1' }}>
                  {correctCount > 0 && (
                    <Box sx={{ width: `${correctCount / totalCount * 100}%`, bgcolor: '#00ba88', height: '100%' }} />
                  )}
                  {wrongCount > 0 && (
                    <Box sx={{ width: `${wrongCount / totalCount * 100}%`, bgcolor: '#ef4444', height: '100%' }} />
                  )}
                  {skippedCount > 0 && (
                    <Box sx={{ width: `${skippedCount / totalCount * 100}%`, bgcolor: '#e2e8f0', height: '100%' }} />
                  )}
                </Box>

                {/* Legend / Stats Details */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#00ba88' }} />
                    <Typography sx={{ fontSize: '13px', fontFamily: 'Poppins', color: '#475569', fontWeight: 600 }}>
                      Correct <span style={{ color: '#94a3b8', marginLeft: '6px' }}>{correctCount.toString().padStart(2, '0')}</span>
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
                    <Typography sx={{ fontSize: '13px', fontFamily: 'Poppins', color: '#475569', fontWeight: 600 }}>
                      Wrong <span style={{ color: '#94a3b8', marginLeft: '6px' }}>{wrongCount.toString().padStart(2, '0')}</span>
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#cbd5e1' }} />
                    <Typography sx={{ fontSize: '13px', fontFamily: 'Poppins', color: '#475569', fontWeight: 600 }}>
                      Skipped <span style={{ color: '#94a3b8', marginLeft: '6px' }}>{skippedCount.toString().padStart(2, '0')}</span>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          )}
        </Box>
      ) : (
        <BeforeStartSummary
          examName={examName}
          examDetails={examDetails}
          exam={exam}
          displayType={displayType}
          rawType={rawType}
          institute={institute}
          formatDate={formatDate}
          totalCount={totalCount}
          guidelines={guidelines}
          isMixedExam={isMixedExam}
          childExams={childExams}
          mixedTotalQuestions={mixedTotalQuestions}
          handleOpenStartModal={handleOpenStartModal}
          startButtonSx={startButtonSx}
          mapChildExamType={mapChildExamType}
          parseInstructions={parseInstructions}
        />
      )}

      {/* Start Exam Pop-up Modal */}
      <StartExamModal
        isOpen={isModalOpen}
        onClose={handleCloseStartModal}
        onStart={handleStartExam}
        duration={activeModalDuration}
        questionsCount={activeModalQuestionsCount}
        examType={activeModalType}
        instructions={activeModalInstructions}
      />

      {/* Start MCQ Exam Confirmation Modal */}
      <ConfirmationModal
        isOpen={isMcqConfirmOpen}
        onClose={() => {
          setIsMcqConfirmOpen(false);
          setSelectedChildExam(null);
        }}
        onConfirm={handleConfirmStartMcq}
        title="Start Quiz"
        description="Are you sure you want to start this quiz? Once started, the timer will begin and cannot be paused."
        confirmText="Start Quiz"
        cancelText="Cancel"
        type="info"
      />

      {/* Exam Result Pop-up Modal */}
      {isCompleted && (
        <ExamResultModal
          isOpen={isResultModalOpen}
          onClose={() => setIsResultModalOpen(false)}
          questions={dynamicResults?.questions || []}
          answers={dynamicResults?.answers || {}}
          examName={examName}
          className={exam.topic || "A/L Bio Class"}
          onCheckPaper={() => setIsResultModalOpen(false)}
        />
      )}

      {/* View Options Pop-up Modal for Essay/Structured exams */}
      <ViewOptionsModal
        isOpen={isViewOptionsModalOpen}
        onClose={() => {
          setIsViewOptionsModalOpen(false);
          setViewOptionsChild(null);
        }}
        onViewAnswers={() => {
          if (viewOptionsChild) {
            const stats = buildResultStats(childExamResults[viewOptionsChild.examId], viewOptionsChild.questionCount || 0);
            const childScorePercentage = stats.scorePercentage || parseMarksPercentage(viewOptionsChild.myMarks);
            navigate(`/exam-manager/answer-breakdown/${classExamId}?examId=${viewOptionsChild.examId}`, {
              state: {
                exam: {
                  ...exam,
                  name: stripHtml(viewOptionsChild.examTitle),
                  type: viewOptionsChild.examType,
                  examId: viewOptionsChild.examId
                },
                results: buildDynamicResultsFromStats({ ...stats, scorePercentage: childScorePercentage })
              }
            });
            return;
          }

          const mainExamId = exam?.examId || examDetails?.examId;
          const query = mainExamId ? `?examId=${mainExamId}` : '';
          navigate(`/exam-manager/answer-breakdown/${classExamId}${query}`, {
            state: {
              exam: { ...exam, name: examName, type: rawType },
              results: dynamicResults
            }
          });
        }}
        examName={viewOptionsChild ? stripHtml(viewOptionsChild.examTitle) : examName}
        paperId={viewOptionsChild?.paperUploadId || exam?.paperUploadId || examDetails?.paperUploadId}
      />

      {/* API Error Toast Notification */}
      <ToastMessage
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </Box>
  );
}
